import type { SiteOrder, SiteOrderLine, SiteOrderStatus } from '@/types/site-order';

/**
 * Presentation logic for the online order desk.
 *
 * Kept out of the pages so the plain node test tier covers it — `apps/admin`
 * tests pure functions only, matching `apps/site`. The POS app carries the same
 * four functions in its own service file; they are deliberately identical,
 * because the same shopkeeper works the same queue from either screen and two
 * apps disagreeing about which button to show is a support call.
 */

/**
 * What the shop can do with an order right now.
 *
 * Mirrors the API's transitions rather than guessing at them, so a button never
 * appears that the server would refuse. The server remains the authority — two
 * people working one queue means this can be seconds stale — but a button
 * certain to fail is worse than no button.
 */
export function availableActions(
  order: Pick<SiteOrder, 'status' | 'fulfilment_mode' | 'bill_id'>,
): {
  accept: boolean;
  reject: boolean;
  ready: boolean;
  complete: boolean;
  contact: boolean;
  close: boolean;
} {
  const enquiry = order.fulfilment_mode === 'ENQUIRY';
  return {
    accept: !enquiry && order.status === 'NEW',
    // From ACCEPTED and READY too: someone who accepts and then finds the shelf
    // empty must not be stuck.
    reject: !enquiry && ['NEW', 'ACCEPTED', 'READY'].includes(order.status),
    ready: !enquiry && order.status === 'ACCEPTED',
    complete: !enquiry && ['ACCEPTED', 'READY'].includes(order.status) && !!order.bill_id,
    contact: enquiry && order.status === 'NEW',
    close: enquiry && ['NEW', 'CONTACTED'].includes(order.status),
  };
}

/** Label and `@bbpos/ui` StatusKey for a status pill. */
export function statusTone(status: SiteOrderStatus): {
  label: string;
  tone: 'brand' | 'success' | 'warning' | 'danger' | 'neutral' | 'parked';
} {
  switch (status) {
    case 'NEW':
      return { label: 'New', tone: 'brand' };
    case 'ACCEPTED':
      return { label: 'Accepted', tone: 'parked' };
    case 'READY':
      return { label: 'Ready', tone: 'warning' };
    case 'COMPLETED':
      return { label: 'Completed', tone: 'success' };
    case 'CLOSED_WON':
      return { label: 'Won', tone: 'success' };
    case 'PENDING_PAYMENT':
      return { label: 'Awaiting payment', tone: 'warning' };
    case 'CONTACTED':
      return { label: 'Contacted', tone: 'brand' };
    case 'REJECTED':
    case 'CANCELLED':
    case 'EXPIRED':
    case 'PAYMENT_ABANDONED':
    case 'CLOSED_LOST':
      return { label: humanise(status), tone: 'danger' };
    default:
      return { label: humanise(status), tone: 'neutral' };
  }
}

function humanise(status: string): string {
  const lower = status.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * How long before the SLA auto-rejects a paid order nobody has touched.
 *
 * Null whenever there is no deadline, which is every pay-on-fulfilment order —
 * nobody's money is waiting, so a countdown would manufacture urgency.
 */
export function slaRemaining(
  order: Pick<SiteOrder, 'auto_reject_at' | 'status'>,
  now: number = Date.now(),
): { minutes: number; overdue: boolean } | null {
  if (!order.auto_reject_at || order.status !== 'NEW') return null;
  const deadline = new Date(order.auto_reject_at).getTime();
  if (!Number.isFinite(deadline)) return null;
  const minutes = Math.round((deadline - now) / 60_000);
  return { minutes: Math.abs(minutes), overdue: minutes < 0 };
}

/**
 * Is this acceptance an amendment or a plain yes?
 *
 * Drives whether the button reads "Accept order" or "Accept 6 of 8" — someone
 * about to short a customer should read it in the button rather than discover
 * it afterwards.
 */
export function amendmentSummary(
  items: Array<Pick<SiteOrderLine, 'id' | 'quantity'>>,
  amendments: Record<string, number>,
): { changed: boolean; supplied: number; ordered: number } {
  let supplied = 0;
  let ordered = 0;
  let changed = false;
  for (const item of items) {
    const accepted = amendments[item.id] ?? item.quantity;
    ordered += item.quantity;
    supplied += accepted;
    if (accepted !== item.quantity) changed = true;
  }
  return { changed, supplied, ordered };
}

/**
 * Turn the API's per-line stock conflict into amendments applied with one tap.
 *
 * The 409 enumerates every short line with what is actually available, so
 * "reduce to what we have" is a pure mapping rather than a second guess at the
 * shop's stock. Never raises a quantity: the server refuses an increase, so a
 * prefill suggesting one would produce a guaranteed failure.
 */
export function amendmentsFromShortfall(
  details: Array<{ field: string; message: string; available?: number }> | undefined,
  items: Array<Pick<SiteOrderLine, 'id' | 'product_id' | 'quantity'>>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const detail of details ?? []) {
    if (detail.message !== 'INSUFFICIENT_STOCK') continue;
    if (typeof detail.available !== 'number') continue;
    for (const item of items) {
      if (item.product_id !== detail.field) continue;
      out[item.id] = Math.max(0, Math.min(item.quantity, detail.available));
    }
  }
  return out;
}
